import { useState, useEffect } from "react";
import { Link } from "@/components/LangLink";
import { Check, ChevronLeft, ArrowRight, CalendarCheck, Ruler, ShieldCheck } from "lucide-react";
import { CTASection, SectionHeading } from "@/components/sections";
import { ProductGallery, RequestInfoModal } from "@/components/product-detail";
import { LUMAVEN_ANTITROMBO as A, LUMAVEN_HEALTH_NOTE } from "@/lib/lumaven";
import { useAssistant } from "@/contexts/AssistantContext";

const LumavenAntitrombo = () => {
  const [exeId, setExeId] = useState(A.executions[0].id);
  const [modalOpen, setModalOpen] = useState(false);
  const exe = A.executions.find((e) => e.id === exeId) || A.executions[0];

  const { setProductCtx } = useAssistant();
  useEffect(() => {
    const exes = A.executions.map((e) => `${e.code} ${e.label}`).join("; ");
    setProductCtx({
      title: A.name,
      url: `/it/prodotti/lumaven/${A.slug}`,
      text: `SCHEDA PRODOTTO: ${A.name} (calza ANTITROMBO, linea separata dalle preventive). ${A.subtitle}.\n${A.intro.join(" ")}\nCaratteristiche: ${A.features.join("; ")}.\nEsecuzioni: ${exes}.\nSistema Soft Touch: ${A.softTouch.text.join(" ")}\nMisure: cB caviglia, cC polpaccio, cG coscia, lD altezza gambaletto, lG altezza coscia/monocollant. Taglie XS–XXL.\nNota: la scelta di esecuzione e taglia spetta al personale sanitario. Colore commerciale, composizione e foto ufficiali non ancora disponibili.`,
    });
    return () => setProductCtx(null);
  }, [setProductCtx]);

  const summary = { Prodotto: A.name, Esecuzione: `${exe.code} — ${exe.label}` };

  return (
    <>
      <section className="pt-[92px] lg:pt-[104px] pb-10 lg:pb-14 bg-white" data-testid="lumaven-antitrombo-page">
        <div className="container-x">
          <nav className="flex items-center gap-1.5 text-sm text-muted-foreground" aria-label="breadcrumb">
            <Link to="/prodotti/lumaven" className="hover:text-green">LUMAVEN</Link>
            <span>/</span>
            <span className="text-navy">Calza antitrombo</span>
          </nav>

          <div className="mt-6 grid gap-10 lg:grid-cols-2">
            <div>
              <ProductGallery key={exeId} images={exe.img ? [exe.img] : []} label="Foto in arrivo" testid="antitrombo-gallery" />
              <p className="mt-3 text-xs italic text-muted-foreground">Esecuzione mostrata: {exe.label}.</p>
            </div>

            <div>
              <SectionHeading
                as="h1"
                size="lg"
                maxW="max-w-none"
                eyebrow="Linea antitrombo"
                title={A.name}
                subtitle={A.subtitle}
              />
              {A.intro.map((p, i) => (
                <p key={i} className="mt-4 text-muted-foreground leading-relaxed">{p}</p>
              ))}

              <div className="mt-5 flex items-start gap-2 rounded-xl border border-green/20 bg-green/5 p-3.5 text-sm text-navy/80" data-testid="antitrombo-note">
                <ShieldCheck size={18} className="mt-0.5 shrink-0 text-green" /><span>{A.note}</span>
              </div>

              {/* Esecuzioni */}
              <div className="mt-8" data-testid="antitrombo-executions">
                <div className="text-sm font-semibold text-navy">Esecuzione</div>
                <div className="mt-3 grid gap-2.5 sm:grid-cols-3">
                  {A.executions.map((e) => {
                    const on = e.id === exeId;
                    return (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setExeId(e.id)}
                        aria-pressed={on}
                        data-testid={`antitrombo-exe-${e.id}`}
                        className={`flex flex-col items-start gap-1 rounded-xl border p-3 text-left transition-colors duration-200 ${on ? "border-green bg-green/10" : "border-[#E7EDF2] hover:border-green"}`}
                      >
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${on ? "text-green-deep" : "text-muted-foreground"}`}>
                          {on && <Check size={12} />}{e.code}
                        </span>
                        <span className="text-sm font-semibold text-navy leading-snug">{e.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-3 text-sm text-navy" data-testid="antitrombo-exe-selected">
                  Esecuzione selezionata: <strong className="font-semibold">{exe.code} — {exe.label}</strong>
                </p>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{exe.desc}</p>
                <div className="mt-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-navy/50">Lunghezze disponibili</span>
                  <ul className="mt-2 space-y-1">
                    {exe.lengths.map((l) => (
                      <li key={l} className="flex items-start gap-2 text-sm text-navy/80"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green" />{l}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button onClick={() => setModalOpen(true)} data-testid="antitrombo-request-info" className="btn-green inline-flex justify-center">
                  Richiedi informazioni <ArrowRight size={16} />
                </button>
                <Link to="/il-metodo#prenota" data-testid="antitrombo-book" className="btn-outline inline-flex justify-center">
                  Prenota una misurazione <CalendarCheck size={16} />
                </Link>
              </div>
              <a href="#misure" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:gap-2.5 transition-all">
                <Ruler size={15} /> Scopri come rilevare le misure
              </a>

              <p className="mt-8 text-xs text-muted-foreground leading-relaxed">{LUMAVEN_HEALTH_NOTE}</p>
            </div>
          </div>

          {/* Caratteristiche principali */}
          <div className="mt-16 grid gap-10 lg:grid-cols-2">
            <div data-testid="antitrombo-features">
              <h2 className="font-serif text-2xl font-bold text-navy">Caratteristiche principali</h2>
              <ul className="mt-5 space-y-2.5">
                {A.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-navy/80"><Check size={17} className="mt-0.5 shrink-0 text-green" />{f}</li>
                ))}
              </ul>
            </div>

            {/* Soft Touch */}
            <div className="rounded-2xl border border-[#E7EDF2] bg-cloud p-7" data-testid="antitrombo-softtouch">
              <h2 className="font-serif text-2xl font-bold text-navy">{A.softTouch.title}</h2>
              {A.softTouch.text.map((p, i) => (
                <p key={i} className="mt-3 text-sm text-muted-foreground leading-relaxed">{p}</p>
              ))}
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-navy/50">{A.softTouch.patentNote}</p>
            </div>
          </div>

          {/* Guida alle misure */}
          <div id="misure" className="mt-16 scroll-mt-28" data-testid="antitrombo-measure-guide">
            <h2 className="font-serif text-2xl font-bold text-navy">Guida alle misure</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {A.measureGuide.map(([code, label]) => (
                <div key={code} className="flex items-center gap-3 rounded-xl border border-[#E7EDF2] p-3.5">
                  <span className="flex h-9 w-11 items-center justify-center rounded-lg bg-navy text-sm font-bold text-white">{code}</span>
                  <span className="text-sm text-navy/80">{label}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
              Per il <strong className="text-navy">gambaletto AD</strong> sono fondamentali cB, cC e lD; per la <strong className="text-navy">calza alla coscia</strong> e il <strong className="text-navy">monocollant AG</strong> sono fondamentali cB, cC, cG e lG. Le misure devono essere rilevate correttamente: in caso di dubbio è consigliata la <Link to="/il-metodo#prenota" className="font-semibold text-green underline underline-offset-2">misurazione professionale MEDISISTEM</Link>.
            </p>
          </div>

          {/* Tabella taglie */}
          <div className="mt-12" data-testid="antitrombo-size-table">
            <h3 className="font-serif text-xl font-bold text-navy">Tabella delle taglie</h3>
            <div className="mt-4 overflow-x-auto rounded-xl border border-[#E7EDF2]">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="bg-navy text-white">
                    <th className="px-4 py-3 text-left font-semibold">Misura</th>
                    {A.sizeTable.cols.map((c) => <th key={c} className="px-4 py-3 text-center font-semibold">{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {A.sizeTable.rows.map((row, ri) => (
                    <tr key={ri} className={ri % 2 ? "bg-cloud" : "bg-white"}>
                      <td className="px-4 py-2.5 font-semibold text-navy">{row[0]}</td>
                      {row.slice(1).map((v, ci) => <td key={ci} className="px-4 py-2.5 text-center text-navy/80">{v}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Lunghezze */}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-[#E7EDF2] p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-navy/50">Lunghezze — calza alla coscia / monocollant (AG)</div>
                <ul className="mt-2 space-y-1 text-sm text-navy/80">
                  <li>Corta: lG inferiore a 71 cm</li><li>Normale: lG da 71 a 84 cm</li><li>Lunga: lG superiore a 84 cm</li>
                </ul>
              </div>
              <div className="rounded-xl border border-[#E7EDF2] p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-navy/50">Lunghezze — gambaletto (AD)</div>
                <ul className="mt-2 space-y-1 text-sm text-navy/80">
                  <li>Corta: lD inferiore a 37 cm</li><li>Normale: lD da 37 a 42 cm</li>
                </ul>
              </div>
            </div>

            {/* Colori identificativi taglia */}
            <div className="mt-6 rounded-xl border border-[#E7EDF2] bg-cloud p-4" data-testid="antitrombo-size-colors">
              <div className="text-sm font-semibold text-navy">Colore identificativo della taglia (sulla punta)</div>
              <div className="mt-3 flex flex-wrap gap-3">
                {A.sizeColors.map(([size, name, hex]) => (
                  <span key={size} className="inline-flex items-center gap-2 rounded-full border border-[#E7EDF2] bg-white px-3 py-1.5 text-xs text-navy/80">
                    <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ backgroundColor: hex }} />
                    <strong className="font-semibold text-navy">{size}</strong> · {name}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-xs italic text-muted-foreground">Questi colori servono a identificare la taglia e non rappresentano i colori disponibili della calza.</p>
            </div>
          </div>

          <Link to="/prodotti/lumaven" className="mt-10 inline-flex items-center gap-1.5 text-sm font-semibold text-green hover:gap-2.5 transition-all">
            <ChevronLeft size={16} /> Torna a LUMAVEN
          </Link>
        </div>
      </section>

      <RequestInfoModal open={modalOpen} onOpenChange={setModalOpen} summary={summary} />
      <CTASection />
    </>
  );
};

export default LumavenAntitrombo;
